import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { Icon } from '@opallabs/lapidary'
import {
  type CalendarFilterCategory,
  type CalendarFilterOption,
  type CalendarFilterState,
  EMPTY_CATEGORY_FILTER,
} from '../types'
import { type UseCalendarFiltersResult } from '../useCalendarFilters'
import { buildValueTree, parentFilterMode } from '../valueTree'
import { isChosen, listsFor, parentState } from './OptionLeaf'
import { OptionRow } from './OptionRow'
import * as styles from './styles'

/** The options of one category that a search found, in the order offered. */
export type CalendarFilterMatches = Readonly<{
  category: CalendarFilterCategory
  options: readonly CalendarFilterOption[]
}>

/**
 * Every option of every category whose label holds `query`, at every depth: a
 * channel and the accounts under it, a label set and its labels.
 *
 * A plain case-insensitive substring, like the search inside a tier. The live
 * window is two dates rather than a list of values, so it has nothing to find.
 */
export const calendarFilterMatches = (
  categories: readonly CalendarFilterCategory[],
  query: string
): CalendarFilterMatches[] => {
  const wanted = query.trim().toLowerCase()
  if (wanted === '') return []

  return categories
    .filter((category) => category.picker !== 'dateRange')
    .map((category) => ({
      category,
      options: category.options.filter((option) =>
        option.label.toLowerCase().includes(wanted)
      ),
    }))
    .filter(({ options }) => options.length > 0)
}

export type SearchResultsProps = Readonly<{
  /** The filter state, so each row reads what its category holds. */
  chosen: CalendarFilterState
  matches: readonly CalendarFilterMatches[]
  onToggleValue: UseCalendarFiltersResult['toggleValue']
  renderOptionIcon?: (
    category: CalendarFilterCategory,
    option: CalendarFilterOption
  ) => React.ReactNode
}>

/**
 * What the search over every category found, in place of the category list:
 * one list per category, and in front of a value the row it sits under.
 *
 * A row here is the row inside the category, read and written the same way. It
 * is written through the category's tree, so a tick lands in the list that
 * the category, or the value's own set, is set to — a label of a hidden set is
 * hidden from here as much as from inside Labels.
 */
export const SearchResults: React.FC<SearchResultsProps> = ({
  chosen: state,
  matches,
  onToggleValue,
  renderOptionIcon,
}) => {
  const found = matches.reduce(
    (total, { options }) => total + options.length,
    0
  )

  return (
    <>
      {/*
        The count, not the list. A live region holding every result reads the
        whole thing out again on each keystroke, which buries the one number a
        person is waiting for.
      */}
      <VisuallyHidden aria-live="polite" role="status">
        {found === 1 ? '1 filter found' : `${found} filters found`}
      </VisuallyHidden>

      {found === 0 ? (
        <p className={styles.selectors.leafEmpty}>No matches</p>
      ) : (
        <ul className={styles.selectors.searchResults}>
          {matches.map(({ category, options }) => {
            const tree = buildValueTree(category.options, category.modeScope)
            const filter = state.cats[category.id] ?? EMPTY_CATEGORY_FILTER
            const modeOf = (valueId: string) =>
              tree.modeScope === 'parent'
                ? parentFilterMode(valueId, filter, tree)
                : (filter.mode ?? 'include')
            const headingId = `calendar-filter-search-${category.id}-heading`

            return (
              <li key={category.id}>
                <h3 className={styles.selectors.searchHeading} id={headingId}>
                  <Icon
                    ariaHidden={true}
                    glyph={category.icon}
                    size={styles.CATEGORY_ICON_SIZE}
                  />
                  <span className={styles.selectors.text}>
                    {category.label}
                  </span>
                </h3>
                <ul
                  aria-labelledby={headingId}
                  className={styles.selectors.optionList}
                >
                  {options.map((option) => {
                    const mode = modeOf(option.id)
                    const lists = listsFor(mode, filter.include, filter.exclude)
                    const { group } = option

                    return (
                      <li key={option.id}>
                        <OptionRow
                          // The leaf pane beside this one can draw the same
                          // option, so the box here takes an id of its own.
                          categoryId={`search-${category.id}`}
                          checked={isChosen(option.id, lists, tree)}
                          icon={renderOptionIcon?.(category, option)}
                          mode={mode}
                          onToggle={() =>
                            onToggleValue(category.id, option.id, tree)
                          }
                          option={option}
                          partChosenState={
                            tree.childrenOf.has(option.id)
                              ? parentState(option.id, lists, tree)
                              : undefined
                          }
                          path={
                            group === undefined || group.id === option.id
                              ? undefined
                              : group.label
                          }
                        />
                      </li>
                    )
                  })}
                </ul>
              </li>
            )
          })}
        </ul>
      )}
    </>
  )
}
SearchResults.displayName = 'Calendar: SearchResults'
